import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Video } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const POSITIONS = ["QB","RB","WR","TE","OL","DL","LB","DB","K","P","ATH"];
const CLASS_YEARS = ["2026","2027","2028","2029","2030","2031"];
const STATUSES = ["uncommitted","interested","committed","signed"];

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [videos, setVideos] = useState([]);
  const [videoForm, setVideoForm] = useState({ title: "", url: "", video_type: "highlight", description: "" });
  const [saving, setSaving] = useState(false);

  const loadProfile = async () => {
    try {
      const { data } = await api.get("/players/me", { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 });
      if (data && data.id) {
        setProfile(data); setForm(data);
        const v = await api.get(`/videos?player_id=${data.id}`);
        setVideos(v.data);
      } else {
        setProfile({}); setForm({ name: user?.name || "", email: user?.email || "" });
      }
    } catch {
      setProfile({}); setForm({ name: user?.name || "", email: user?.email || "" });
    }
  };
  useEffect(() => { if (user) loadProfile(); }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/players", {
        ...form,
        gpa: form.gpa ? parseFloat(form.gpa) : null,
        highlight_urls: form.highlight_urls || [],
        stats: form.stats || {},
        awards: (typeof form.awards === "string" ? form.awards.split(",").map(a=>a.trim()).filter(Boolean) : form.awards) || [],
      });
      setProfile(data);
      toast.success("Profile saved.");
      loadProfile();
    } catch (e) { toast.error("Failed to save profile."); }
    setSaving(false);
  };

  const addVideo = async (e) => {
    e.preventDefault();
    try {
      await api.post("/videos", { ...videoForm, player_id: profile?.id });
      toast.success("Video added.");
      setVideoForm({ title: "", url: "", video_type: "highlight", description: "" });
      loadProfile();
    } catch { toast.error("Failed to add video."); }
  };

  const removeVideo = async (id) => {
    await api.delete(`/videos/${id}`);
    toast.success("Video removed.");
    loadProfile();
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  if (!user) return null;

  return (
    <div className="section-surface min-h-screen py-16">
      <div className="container-app">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="label-tag mb-2" style={{ color: "#000" }}>Dashboard</div>
            <h1 className="font-heading font-black uppercase text-4xl md:text-6xl leading-none">Hey, {user.name.split(" ")[0]}.</h1>
            <p className="mt-3 text-neutral-600">Manage your recruiting profile, film, and stats.</p>
          </div>
          {profile?.id && (
            <Link to={`/players/${profile.id}`} className="btn-ghost-dark" data-testid="view-public-profile">View Public Profile</Link>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={save} className="card-light p-8 lg:col-span-2 space-y-8">
            <div className="font-heading font-black uppercase text-xl">Player Profile</div>
            <div className="grid md:grid-cols-2 gap-6">
              <F label="Full Name" v={form.name} onC={(v)=>set("name",v)} testid="pp-name" />
              <F label="School" v={form.school} onC={(v)=>set("school",v)} testid="pp-school" />
              <SelectF label="Class Year" v={form.class_year} onC={(v)=>set("class_year",v)} opts={CLASS_YEARS} testid="pp-class" />
              <SelectF label="Position" v={form.position} onC={(v)=>set("position",v)} opts={POSITIONS} testid="pp-position" />
              <F label="Height (e.g. 6'2)" v={form.height} onC={(v)=>set("height",v)} testid="pp-height" />
              <F label="Weight (lbs)" v={form.weight} onC={(v)=>set("weight",v)} testid="pp-weight" />
              <F label="GPA" v={form.gpa} onC={(v)=>set("gpa",v)} testid="pp-gpa" />
              <F label="Location (City, ST)" v={form.location} onC={(v)=>set("location",v)} testid="pp-location" />
              <F label="Phone" v={form.phone} onC={(v)=>set("phone",v)} testid="pp-phone" />
              <F label="Contact Email" v={form.email} onC={(v)=>set("email",v)} testid="pp-email" />
              <F label="Hudl URL" v={form.hudl_url} onC={(v)=>set("hudl_url",v)} testid="pp-hudl" />
              <F label="Photo URL (or upload below)" v={form.photo_url} onC={(v)=>set("photo_url",v)} testid="pp-photo" />
              <SelectF label="Recruiting Status" v={form.recruiting_status} onC={(v)=>set("recruiting_status",v)} opts={STATUSES} testid="pp-status" />
              <F label="Awards (comma separated)" v={Array.isArray(form.awards) ? form.awards.join(", ") : form.awards} onC={(v)=>set("awards",v)} testid="pp-awards" />
            </div>
            <div>
              <ImageUpload label="Or Upload Your Profile Photo" value={form.photo_url} onChange={(v)=>set("photo_url", v)} testid="pp-photo-upload" />
            </div>
            <div>
              <label className="label-tag" style={{ color: "#000" }}>Bio</label>
              <textarea value={form.bio || ""} onChange={(e)=>set("bio", e.target.value)} rows={4}
                className="mt-2 w-full border border-black/15 bg-white p-3 focus:border-[color:var(--gold)] outline-none" data-testid="pp-bio" />
            </div>
            <button disabled={saving} className="btn-gold" data-testid="pp-save">{saving?"Saving…":"Save Profile"}</button>
          </form>

          <div className="space-y-6">
            <div className="card-light p-8">
              <div className="font-heading font-black uppercase text-xl mb-6 flex items-center gap-2"><Video size={20}/> Highlight Videos</div>
              <form onSubmit={addVideo} className="space-y-4">
                <input required placeholder="Title (e.g. Junior Season Highlights)" value={videoForm.title} onChange={(e)=>setVideoForm({...videoForm, title:e.target.value})} className="input-sharp" data-testid="video-title" />
                <input required placeholder="Hudl or YouTube URL" value={videoForm.url} onChange={(e)=>setVideoForm({...videoForm, url:e.target.value})} className="input-sharp" data-testid="video-url" />
                <select value={videoForm.video_type} onChange={(e)=>setVideoForm({...videoForm, video_type:e.target.value})} className="input-sharp" data-testid="video-type">
                  <option value="highlight">Highlight</option>
                  <option value="game">Game Film</option>
                  <option value="workout">Workout</option>
                </select>
                <button className="btn-gold" data-testid="video-add-btn"><Plus size={16}/> Add Video</button>
              </form>
              <div className="mt-6 space-y-3">
                {videos.map(v => (
                  <div key={v.id} className="p-3 border border-black/10 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-heading uppercase font-bold text-sm">{v.title}</div>
                      <div className="text-xs text-neutral-500 break-all">{v.url}</div>
                    </div>
                    <button onClick={()=>removeVideo(v.id)} className="text-red-500 hover:text-red-700" data-testid={`video-remove-${v.id}`}><Trash2 size={16}/></button>
                  </div>
                ))}
                {videos.length === 0 && <div className="text-neutral-400 text-sm">No videos yet.</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, v, onC, testid }) {
  return (
    <div>
      <label className="label-tag" style={{ color: "#000" }}>{label}</label>
      <input value={v || ""} onChange={(e)=>onC(e.target.value)} className="input-sharp mt-2" data-testid={testid} />
    </div>
  );
}
function SelectF({ label, v, onC, opts, testid }) {
  return (
    <div>
      <label className="label-tag" style={{ color: "#000" }}>{label}</label>
      <select value={v || ""} onChange={(e)=>onC(e.target.value)} className="input-sharp mt-2" data-testid={testid}>
        <option value="">—</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
